/**
 * Sprint 24.5 — InstallStepTest (Custom in-house MCP, DEV ONLY).
 *
 * Registers the connection from the template (which runs `connections/add`'s
 * built-in discovery test) and reflects the result. On success it advances to the
 * success step; on failure it shows the error with a retry.
 */

import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { FirstPartyTemplate } from "../../../../hooks/use-first-party-templates";

export interface InstallStepTestProps {
  template: FirstPartyTemplate;
  /** Registers + tests; resolves with the outcome. Implemented by the modal. */
  runTest: () => Promise<{ ok: boolean; error?: string }>;
  onTested: () => void;
  onCancel: () => void;
}

export function InstallStepTest({ template, runTest, onTested, onCancel }: InstallStepTestProps) {
  const [phase, setPhase] = useState<"testing" | "error">("testing");
  const [errorMsg, setErrorMsg] = useState("");
  const startedRef = useRef(false);

  async function run() {
    setPhase("testing");
    setErrorMsg("");
    try {
      const result = await runTest();
      if (result.ok) {
        onTested();
      } else {
        setPhase("error");
        setErrorMsg(result.error ?? "Couldn't reach the MCP server.");
      }
    } catch (err) {
      setPhase("error");
      setErrorMsg(err instanceof Error ? err.message : "Couldn't reach the MCP server.");
    }
  }

  // Kick off the test once on mount.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={body}>
      <div style={center}>
        <span style={phase === "error" ? warnRing : ring}>
          <i
            className={`ti ${phase === "error" ? "ti-alert-triangle" : "ti-loader-2"}`}
            style={phase === "testing" ? spin : undefined}
            aria-hidden="true"
          />
        </span>
        {phase === "error" ? (
          <>
            <h3 style={h3}>Connection test failed</h3>
            <p style={sub}>{errorMsg}</p>
          </>
        ) : (
          <>
            <h3 style={h3}>Testing {template.display_name}…</h3>
            <p style={sub}>Registering the server and discovering its tools.</p>
          </>
        )}
      </div>

      <div style={footer}>
        <button type="button" style={cancelLink} onClick={onCancel}>
          Cancel
        </button>
        {phase === "error" && (
          <button type="button" style={primaryBtn} onClick={() => void run()}>
            Retry
          </button>
        )}
      </div>

      <style>{keyframes}</style>
    </div>
  );
}

const keyframes = `@keyframes kuvio-test-spin { to { transform: rotate(360deg); } }`;

const body: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 14,
  padding: "24px 28px",
  fontFamily: "var(--font-sans)",
};

const center: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  gap: 8,
  padding: "8px 0",
};

const ring: CSSProperties = {
  width: 56,
  height: 56,
  borderRadius: "50%",
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 24,
  flexShrink: 0,
};

const warnRing: CSSProperties = { ...ring, background: "#FAEEDA", color: "#412402" };

const spin: CSSProperties = { animation: "kuvio-test-spin 0.9s linear infinite" };

const h3: CSSProperties = { fontSize: 16, fontWeight: 500, margin: 0, color: "var(--color-text-primary)" };
const sub: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  margin: 0,
  maxWidth: 320,
  lineHeight: 1.5,
};

const footer: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: 4,
};

const cancelLink: CSSProperties = {
  background: "transparent",
  border: "none",
  padding: 0,
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  fontSize: 12,
  fontFamily: "var(--font-sans)",
};

const primaryBtn: CSSProperties = {
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-text-primary)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
